'use client'

import { MouseEvent, useCallback, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'

import ResponsiveSection from '@/components/common/ResponsiveSection'
import useGetMenuProducts from '@/hooks/useGetMenuProducts'
import useLazyCategoryRender from '@/hooks/useLazyCategoryRender'
import useProductImageState from '@/hooks/useProductImageState'
import EventEmitter from '@/utils/eventEmitter'
import CategoriesNavigation from './CategoriesNavigation'
import Category from './Category'
import { pauseScrollTracking } from '@/components/sections/MarketingPopup/PageScrollTracker'
import { useSearchParams } from 'next/navigation'

const PRODUCTS_HORIZONTAL_NAV_CHANNEL = 'PRODUCTS_HORIZONTAL_NAV_CHANNEL'

const ProductSection = () => {
  const { isTopNavLayout } = useProductImageState()
  const initialLoadRef = useRef(false)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const searchParams = useSearchParams()

  const { data, isSuccess } = useGetMenuProducts()

  const menu = data?.payload?.tags

  const {
    shouldShowCategoryDetails,
    shouldShowCategorySkeleton,
    showCategorySkeleton,
    showCategoryDetails,
    registerCategoryInViewport,
    unregisterCategoryFromViewport,
  } = useLazyCategoryRender({ menu })
  const categoryVisibilityEmitter = useMemo(() => EventEmitter(), [])

  const hideCategoriesNavigation = menu?.length === 1

  // Simple function to get category by id
  const getCategory = useCallback(
    (id?: string) => menu?.find((cat) => id?.includes(cat.id)),
    [menu]
  )

  // Simplified Safari-compatible scroll function
  const scrollToCategory = useCallback((catId: string) => {
    const categoryAnchor = document.getElementById(catId)
    if (!categoryAnchor) return

    // Pause scroll tracking to prevent lag during smooth scroll
    pauseScrollTracking(1500)

    // Clean up previous timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    // Safari-optimized scroll function
    const performScroll = () => {
      categoryAnchor.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }

    // Simple delay-based approach - more reliable than complex observers
    const handleContentLoading = () => {
      // Initial scroll attempt
      performScroll()

      // If content is still loading (category not fully visible), retry
      scrollTimeoutRef.current = setTimeout(() => {
        const rect = categoryAnchor.getBoundingClientRect()
        const isFullyVisible =
          rect.top >= 0 && rect.top < window.innerHeight * 0.8

        if (!isFullyVisible) {
          performScroll()

          // One final retry if needed
          scrollTimeoutRef.current = setTimeout(() => {
            const finalRect = categoryAnchor.getBoundingClientRect()
            if (finalRect.top < 0 || finalRect.top > window.innerHeight * 0.8) {
              performScroll()
            }
          }, 500)
        }
      }, 300)
    }

    handleContentLoading()
  }, [])

  // Enhanced click handler that loads intermediate categories
  const onCategoryNavItemClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement> | null, catId: string) => {
      e?.preventDefault()
      // Find the target category index
      const targetIndex = menu?.findIndex((cat) => cat.id === catId) ?? -1
      if (targetIndex === -1 || !menu) return

      // Load all categories up to the target to ensure proper scroll positioning
      for (let i = 0; i <= targetIndex; i++) {
        const category = menu[i]
        if (category) {
          showCategorySkeleton(category.id)
        }
      }

      scrollToCategory(catId)
    },
    [menu, scrollToCategory, showCategorySkeleton]
  )

  useEffect(() => {
    if (isSuccess) {
      const catId = decodeURIComponent(window.location.hash)
      if (searchParams?.get('debugging')) {
        console.log(catId, isSuccess)
      }
      if (catId) {
        onCategoryNavItemClick(null, catId.replace('#', ''))
      }
    }
  }, [isSuccess, onCategoryNavItemClick])

  // Initial load effect
  // useEffect(() => {
  //   if (!menu || initialLoadRef.current || !isSuccess) return
  //   console.log('aaaaa')
  //   if (menu.length > 0) {
  //     // If no category in URL hash, load the first category
  //     const firstCategory = menu[0]
  //     if (firstCategory?.id) {
  //       showCategorySkeleton(firstCategory.id)
  //       // We don't need to call showCategoryDetails here since it's handled by the hook's initial state
  //     }
  //   }
  //   initialLoadRef.current = true
  // }, [menu, showCategorySkeleton, isSuccess])

  // Portal container creation
  useEffect(() => {
    if (typeof window === 'undefined') return

    const createPortalContainer = () => {
      let portalContainer = document.getElementById(
        PRODUCTS_HORIZONTAL_NAV_CHANNEL
      )
      if (!portalContainer) {
        portalContainer = document.createElement('div')
        portalContainer.id = PRODUCTS_HORIZONTAL_NAV_CHANNEL
        document.body.appendChild(portalContainer)
      }
    }

    createPortalContainer()
  }, [])

  // Simple computed values
  const showSideNav = !isTopNavLayout && !hideCategoriesNavigation
  const showHorizontalNav = isTopNavLayout && !hideCategoriesNavigation
  const productSectionSize = showSideNav ? 9 : 12

  return (
    <div className="px-0 py-6 lg:py-8">
      {typeof window !== 'undefined' &&
        createPortal(
          <div id={`${PRODUCTS_HORIZONTAL_NAV_CHANNEL}-content`} />,
          document.getElementById(PRODUCTS_HORIZONTAL_NAV_CHANNEL) ||
            document.body
        )}
      <ResponsiveSection className="mb-0 mt-0 p-0">
        <ResponsiveSection.Container>
          <div className="ml-auto mr-auto w-full">
            <div className="flex flex-wrap" id="product-section-mobile">
              <CategoriesNavigation
                syncScroll
                hideCategoriesNavigation={hideCategoriesNavigation}
                showHorizontalNav={showHorizontalNav}
                categories={menu}
                categoryVisibilityEmitter={categoryVisibilityEmitter}
                productSectionSize={productSectionSize}
                onClickItem={onCategoryNavItemClick}
              >
                {menu?.map((category, index) => (
                  <Category
                    key={category.id}
                    categoryVisibilityEmitter={categoryVisibilityEmitter}
                    category={category}
                    isCategoryLoaded={shouldShowCategoryDetails(category.id)}
                    isCategoryDisplayed={shouldShowCategorySkeleton(
                      category.id
                    )}
                    showHorizontalNav={showHorizontalNav}
                    showSideNav={showSideNav}
                    showCategoryDetails={showCategoryDetails}
                    registerCategoryInViewport={registerCategoryInViewport}
                    unregisterCategoryFromViewport={
                      unregisterCategoryFromViewport
                    }
                    index={index}
                  />
                ))}
              </CategoriesNavigation>
            </div>
          </div>
        </ResponsiveSection.Container>
      </ResponsiveSection>
    </div>
  )
}

export default ProductSection
